import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useSettings } from '../../contexts/SettingsContext';
import type { BankInformation } from '../../types/settings';

const bankSchema = z.object({
  bank_name: z.string().min(1, 'Bank name is required'),
  account_name: z.string().min(1, 'Account name is required'),
  account_number: z.string().min(1, 'Account number is required'),
  sort_code: z.string().optional(),
  swift_code: z.string().optional(),
  is_primary: z.boolean().default(false),
  is_public: z.boolean().default(true)
});

type BankFormData = z.infer<typeof bankSchema>;

export default function BankInformationForm() {
  const { state, updateBankInformation, addBankInformation, deleteBankInformation } = useSettings();
  const [editingBank, setEditingBank] = useState<BankInformation | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm<BankFormData>({
    resolver: zodResolver(bankSchema),
    defaultValues: {
      bank_name: '',
      account_name: '',
      account_number: '',
      sort_code: '',
      swift_code: '',
      is_primary: false,
      is_public: true
    }
  });

  const handleEdit = (bank: BankInformation) => {
    setEditingBank(bank);
    reset({
      bank_name: bank.bank_name,
      account_name: bank.account_name,
      account_number: bank.account_number,
      sort_code: bank.sort_code || '',
      swift_code: bank.swift_code || '',
      is_primary: bank.is_primary,
      is_public: bank.is_public
    });
    setIsAdding(false);
  };

  const handleAdd = () => {
    setEditingBank(null);
    reset({
      bank_name: '',
      account_name: '',
      account_number: '',
      sort_code: '',
      swift_code: '',
      is_primary: false,
      is_public: true
    });
    setIsAdding(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this bank information?')) {
      try {
        await deleteBankInformation(id);
      } catch (error) {
        console.error('Error deleting bank information:', error);
      }
    }
  };

  const onSubmit = async (data: BankFormData) => {
    try {
      if (editingBank) {
        await updateBankInformation({
          ...editingBank,
          bank_name: data.bank_name,
          account_name: data.account_name,
          account_number: data.account_number,
          sort_code: data.sort_code,
          swift_code: data.swift_code,
          is_primary: data.is_primary,
          is_public: data.is_public
        });
      } else {
        await addBankInformation({
          bank_name: data.bank_name,
          account_name: data.account_name,
          account_number: data.account_number,
          sort_code: data.sort_code,
          swift_code: data.swift_code,
          is_primary: data.is_primary,
          is_public: data.is_public
        });
      }
      
      setEditingBank(null);
      setIsAdding(false);
      reset();
    } catch (error) {
      console.error('Error saving bank information:', error);
    }
  };

  const cancelEdit = () => {
    setEditingBank(null);
    setIsAdding(false);
    reset();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-golf-gray">Bank Information</h2>
        <button
          onClick={handleAdd}
          className="flex items-center space-x-2 px-4 py-2 bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          <span>Add Bank Account</span>
        </button>
      </div>

      {(isAdding || editingBank) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-golf-gray mb-4">
            {editingBank ? 'Edit Bank Information' : 'Add New Bank Information'}
          </h3>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bank Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('bank_name')}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                />
                {errors.bank_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.bank_name.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('account_name')}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                />
                {errors.account_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.account_name.message}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('account_number')}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                />
                {errors.account_number && (
                  <p className="mt-1 text-sm text-red-600">{errors.account_number.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sort Code
                </label>
                <input
                  type="text"
                  {...register('sort_code')}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                />
                {errors.sort_code && (
                  <p className="mt-1 text-sm text-red-600">{errors.sort_code.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SWIFT Code
                </label>
                <input
                  type="text"
                  {...register('swift_code')}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                />
                {errors.swift_code && (
                  <p className="mt-1 text-sm text-red-600">{errors.swift_code.message}</p>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_primary"
                  {...register('is_primary')}
                  className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange"
                />
                <label htmlFor="is_primary" className="ml-2 block text-sm text-gray-700">
                  Primary bank account
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_public"
                  {...register('is_public')}
                  className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange"
                />
                <label htmlFor="is_public" className="ml-2 block text-sm text-gray-700">
                  Publicly visible
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={cancelEdit}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90"
              >
                {editingBank ? 'Update Bank Information' : 'Add Bank Information'}
              </button>
            </div>
          </form>
        </div>
      )}

      {state.loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
        </div>
      ) : state.error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {state.error}
        </div>
      ) : (
        <div className="space-y-6">
          {state.bankInformation.length > 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bank Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Account Details
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {state.bankInformation.map((bank) => (
                      <tr key={bank.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{bank.bank_name}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{bank.account_name}</div>
                          <div className="text-sm text-gray-500">
                            {bank.account_number}
                            {bank.sort_code && ` • Sort Code: ${bank.sort_code}`}
                            {bank.swift_code && ` • SWIFT: ${bank.swift_code}`}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col space-y-1">
                            {bank.is_primary && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Primary
                              </span>
                            )}
                            {bank.is_public && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Public
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleEdit(bank)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <PencilIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(bank.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <p className="text-gray-500">No bank information found. Add your first bank account using the button above.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}